import { YieldFetcher } from '../../types'

/**
 * Yield Basis (docs.yieldbasis.com) — `yb-<ASSET>` leveraged-liquidity
 * markets on Ethereum. Each market is a 2×-leveraged Curve Cryptoswap LP
 * with impermanent loss cancelled at the depositor level, so the share
 * tracks its asset 1:1 and the yield is the pool's trading fees net of
 * rebalancing cost.
 *
 * Rate source: **Yield Basis's own analytics API** — one unauthenticated
 * call covering every market. `tradingApy` is the trailing-30-day
 * annualised growth of `pricePerShare` (the dashboard's "FT APY (30D)"),
 * 1e18-scaled as a decimal. `tradingApyAllTime` is the same measure over
 * the market's life and is the fallback when a market is too young for a
 * 30-day window.
 *
 * **We publish the 30-day figure ON PURPOSE, and it will not match
 * yieldbasis.com/earn.** Their EARN page headlines `tradingApyAllTime` —
 * verified 2026-08-10 as 3.33 / 2.94 / 1.95 / 5.16 % across WBTC / cbBTC /
 * tBTC / WETH, an exact two-decimal match — while our 30-day reading the
 * same minute was −0.37 / +1.59 / −0.15 / −1.78 %. The divergence is
 * deliberate: every other row in our table is a *current* rate, so a
 * lifetime average dropped into that column would be the
 * apples-to-oranges comparison, and a market's whole-life average drifts
 * further from present conditions the longer it runs. Reviewed and
 * confirmed 2026-08-10 — do not "fix" this to match their UI without
 * re-taking that decision.
 *
 * There is **no DefiLlama yields pool** for this protocol, so the usual
 * third leg does not exist. Instead the fallback re-derives the rate from
 * the API's own `ppsRaw` snapshot series on a *different* route, which
 * survives a partial outage of the APY route.
 *
 * ## APR vs APY
 *
 * The field is named "APY" but is a plain annualisation of realised PPS
 * growth, and at these magnitudes the two conventions are
 * indistinguishable — reconstructing the 30-day growth from the snapshot
 * series on 2026-08-10 gave, per market, simple vs compounded of
 * −0.49/−0.49, +1.47/+1.48, −0.26/−0.26 and −1.82/−1.80 % against API
 * values of −0.50, +1.46, −0.26 and −1.83. Every pair agrees inside
 * 0.03 pp, well under the noise in a trailing realised rate, so the value
 * is passed through unconverted (the Strata `apr` precedent).
 *
 * ## Negative rates are normal — never clamp
 *
 * This is a leveraged strategy, not a yield-accrual vault: when
 * rebalancing slippage outruns trading fees the share price *falls* in
 * asset terms. Three of the four markets printed a negative trailing-30d
 * figure at integration, and the longer-lived earlier market generations
 * were negative on 23–45 % of all days. The values are reported verbatim,
 * exactly as Strata's junior tranches are; a consumer ranking by rate
 * should read them next to the fact that the row is
 * `yieldProfile: 'volatile'`.
 *
 * Emissions are deliberately NOT folded in. Gauge staking pays YB
 * (3.3–6.4 % APR at integration) but **forfeits PPS growth entirely** —
 * it is an alternative to this rate, not an addition, so summing them
 * would describe a position nobody can hold.
 */

const YB_API = 'https://api.yieldbasis.com/v1/analytics'
const TRADING_APY_URL = `${YB_API}/markets/trading-apy?chainId=1`
/** Daily buckets; the hourly default caps the span well under 30 days. */
const SNAPSHOT_URL = (idx: number) =>
  `${YB_API}/markets/snapshots/1/${idx}?window=all&timeframeSeconds=86400`

const TIMEOUT_MS = 8000
const DAY = 86_400
const YEAR_DAYS = 365

/**
 * Yield keys — the token-list `assetGroup` each LT will carry once
 * published (`name::symbol`, and both are derived on-chain from the
 * asset's own symbol). Registered here in that exact shape so the
 * binding keeps working across the publish, as we did for Strata.
 *
 * Note the group is NOT unique across market generations: a deprecated
 * v2 `yb-cbBTC` produces the identical `name::symbol` pair. That is
 * harmless — `resolveEntryApr` maps registry *addresses* to keys and only
 * the current markets are registered — but it is why the deprecated rows
 * must leave the token list rather than sit alongside the new ones.
 */
export const YB_WBTC_KEY = 'Yield Basis liquidity for WBTC::yb-WBTC'
export const YB_CBBTC_KEY = 'Yield Basis liquidity for cbBTC::yb-cbBTC'
export const YB_TBTC_KEY = 'Yield Basis liquidity for tBTC::yb-tBTC'
export const YB_WETH_KEY = 'Yield Basis liquidity for WETH::yb-WETH'

/**
 * Factory market index → yield key. **Only the current generation.**
 * `Factory.market_count()` is 11; indices 0–6 are superseded markets that
 * are withdraw-only but still hold funds and still report a rate, so
 * matching on the asset token instead of the index would bind a live row
 * to a dead market's yield.
 */
const YB_MARKETS: { idx: number; key: string }[] = [
  { idx: 7, key: YB_WBTC_KEY },
  { idx: 8, key: YB_CBBTC_KEY },
  { idx: 9, key: YB_TBTC_KEY },
  { idx: 10, key: YB_WETH_KEY },
]

const getJson = async (url: string): Promise<any> => {
  const res = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(TIMEOUT_MS),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

/** 1e18-scaled decimal → percent. Signed; `-4.9e15` → `-0.49`. */
const toPercent = (raw: unknown): number | undefined => {
  const n = Number(raw)
  return Number.isFinite(n) ? n / 1e16 : undefined
}

/**
 * Primary leg: the trailing-30d figure per market, falling back to the
 * all-time one when the 30d value is missing (a market younger than the
 * window reports nothing rather than a partial-period rate).
 */
const fetchFromApyRoute = async (): Promise<Record<number, number>> => {
  const rows: any[] = (await getJson(TRADING_APY_URL))?.data ?? []
  if (rows.length === 0) throw new Error('yield-basis trading-apy empty')

  const latest = new Map<number, any>()
  for (const r of rows) {
    const idx = Number(r?.marketId)
    if (!Number.isFinite(idx)) continue
    const prev = latest.get(idx)
    if (!prev || Number(r.bucketStart) > Number(prev.bucketStart)) {
      latest.set(idx, r)
    }
  }

  const out: Record<number, number> = {}
  for (const { idx } of YB_MARKETS) {
    const row = latest.get(idx)
    if (!row) continue
    const v = toPercent(row.tradingApy) ?? toPercent(row.tradingApyAllTime)
    if (v !== undefined) out[idx] = v
  }
  if (Object.keys(out).length === 0) {
    throw new Error('yield-basis trading-apy has no current markets')
  }
  return out
}

/**
 * Fallback leg: re-derive the rate from the raw `pricePerShare` series on
 * the snapshots route. Simple (nominal) annualisation of the growth over
 * the trailing 30 days, or the whole series when it is shorter — which is
 * the package convention and, per the header, within 0.03 pp of the
 * compounded reading at these magnitudes.
 */
const fetchFromSnapshots = async (idx: number): Promise<number> => {
  const rows: any[] = (await getJson(SNAPSHOT_URL(idx)))?.data ?? []
  const series = rows
    .filter((r) => r?.ppsRaw && Number(r.ppsRaw) > 0)
    .map((r) => ({ t: Number(r.bucketStart), pps: Number(r.ppsRaw) }))
    .filter((r) => Number.isFinite(r.t) && Number.isFinite(r.pps))
    .sort((a, b) => a.t - b.t)
  if (series.length < 2) throw new Error(`yield-basis snapshots empty ${idx}`)

  const last = series[series.length - 1]
  const target = last.t - 30 * DAY
  // The oldest sample at-or-after the target, else the oldest we have.
  const start = series.find((r) => r.t >= target) ?? series[0]
  const days = (last.t - start.t) / DAY
  if (days <= 0) throw new Error(`yield-basis snapshot span zero ${idx}`)

  return (last.pps / start.pps - 1) * (YEAR_DAYS / days) * 100
}

export const yieldBasisFetcher: YieldFetcher = {
  label: 'YIELD_BASIS',
  fetch: async () => {
    const primary = await fetchFromApyRoute().catch(
      () => ({}) as Record<number, number>,
    )

    const missing = YB_MARKETS.filter((m) => primary[m.idx] === undefined)
    const derived = await Promise.all(
      missing.map((m) =>
        fetchFromSnapshots(m.idx).catch(() => undefined as number | undefined),
      ),
    )
    missing.forEach((m, i) => {
      const v = derived[i]
      if (v !== undefined) primary[m.idx] = v
    })

    const out: Record<string, number> = {}
    for (const { idx, key } of YB_MARKETS) {
      // 0 for an unresolved market rather than omitting the key — the
      // binding treats a missing key as 0 anyway, and an explicit entry
      // keeps the result shape stable for tests.
      out[key] = primary[idx] ?? 0
    }
    return out
  },
}
